import React, { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress, { circularProgressClasses } from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import api from '../../../api/apiSettings';
import { usePrevious } from '../../../utils/hooks';
import colors from '../../../scss/colors';

const autoCompleteStyles = {
  width: '100%',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  '.MuiOutlinedInput-root .MuiAutocomplete-input': {
    padding: 0
  },
  '.MuiFormControl-root': {
    padding: 0
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    padding: '0'
  },
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: colors.NAVY_BLUE
  }
};

function SearchLocation(props) {
  const { changeHandler, value, withClear = true, placeholder, disabled, handleWhenCleared } = props;

  const { t } = useTranslation();

  const initLoadingText = t('dashboard.filter.location');
  const [autoCompleteLocations, setAutoCompleteLocations] = useState([]);
  const [waitingForLocations, setWaitingForLocations] = useState(false);
  const [loadingTextForLocations, setLoadingTextForLocations] = useState(initLoadingText);
  const [textQuery, setTextQuery] = useState('');
  const prevTextQuery = usePrevious(textQuery);
  const [timerId, setTimer] = useState(null);

  const fetchLocationSuggestions = (search) => api.get(`v1/locations?keyword=${search}`).then((res) => res?.data?.Data);

  const withDebounce = (func, delay) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    const timerDelayId = setTimeout(() => {
      setTimer(null);
      func();
    }, delay);
    setTimer(timerDelayId);
  };

  const onInputHandler = (e) => {
    if (!e.target.value) {
      setWaitingForLocations(false);
      setAutoCompleteLocations([]);
      return;
    }
    if (e.target.value.length > 2) {
      setLoadingTextForLocations('Loading...');
      setTextQuery(e.target.value);
      if (e.target.value !== prevTextQuery && e.target.value.includes(prevTextQuery) && autoCompleteLocations.length) {
        setTimeout(() => {
          const newAutocomplite = autoCompleteLocations.filter((item) =>
            item.label.toLowerCase().startsWith(e.target.value.toLowerCase())
          );
          setAutoCompleteLocations(newAutocomplite);
          setWaitingForLocations(false);
        }, 0);
      } else {
        fetchLocationSuggestions(e.target.value).then((res) => {
          setAutoCompleteLocations(res.map((y) => ({ label: y.CityName, id: y.Id })));
          setWaitingForLocations(false);
        });
      }
    } else {
      setLoadingTextForLocations(initLoadingText);
      setAutoCompleteLocations([]);
    }
    setWaitingForLocations(true);
  };

  return (
    <Autocomplete
      disabled={disabled}
      freeSolo
      loading={waitingForLocations}
      loadingText={loadingTextForLocations}
      clearIcon={withClear ? <CloseIcon fontSize="10px" /> : false}
      clearOnBlur
      disablePortal
      id="jobs-filter-location"
      options={autoCompleteLocations}
      // eslint-disable-next-line
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.label}
        </li>
      )}
      isOptionEqualToValue={(option, currentValue) => option.label === currentValue}
      value={value}
      onBlur={() => setAutoCompleteLocations([])}
      onChange={(e, val) => {
        changeHandler(val ? val.label : '');
        if (!val) handleWhenCleared();
      }}
      onInput={(e) => withDebounce(() => onInputHandler(e), 400)}
      sx={autoCompleteStyles}
      renderInput={(params) => (
        <TextField
          sx={{ paddingLeft: '30px' }}
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.inputProps.value.length > 2 && waitingForLocations ? (
                  <CircularProgress
                    variant="indeterminate"
                    disableShrink
                    sx={{
                      maxHeight: '100%',
                      animationDuration: '950ms',
                      position: 'absolute',
                      right: 0,
                      [`& .${circularProgressClasses.svg}`]: {
                        left: 0,
                        top: 0
                      }
                    }}
                    size={25}
                    thickness={4}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
}

export default SearchLocation;
